import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSearchParams } from 'react-router-dom';
import Header from '../components/Header';
import AccountSettings from '../components/account/AccountSettings';
import BillingSection from '../components/account/BillingSection';
import PaymentSettings from '../components/account/PaymentSettings';
import TeamManagement from '../components/account/TeamManagement';
import {
  Settings,
  CreditCard,
  Wallet,
  Users,
  LogIn,
} from 'lucide-react';

const tabs = [
  { id: 'account', label: 'Account Settings', icon: Settings },
  { id: 'billing', label: 'Billing', icon: CreditCard },
  { id: 'payment', label: 'Payment Settings', icon: Wallet },
  { id: 'team', label: 'Team Management', icon: Users },
];

const validTabIds = tabs.map(t => t.id);

const AccountDashboard = () => {
  const { isLoggedIn, user, login } = useAuth();
  const [searchParams] = useSearchParams();
  const initialTab = validTabIds.includes(searchParams.get('tab')) ? searchParams.get('tab') : 'account';
  const [activeTab, setActiveTab] = useState(initialTab);
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && validTabIds.includes(tab)) {
      setActiveTab(tab);
    }
  }, [searchParams]);

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-page">
        <Header />
        <div className="flex items-center justify-center min-h-[calc(100vh-73px)]">
          <div className="text-center space-y-6 max-w-md px-6">
            <div className="w-20 h-20 rounded-2xl mx-auto flex items-center justify-center" style={{ background: 'rgba(249,115,22,0.1)', border: '1px solid rgba(249,115,22,0.2)' }}>
              <LogIn size={36} style={{ color: '#F97316' }} />
            </div>
            <h1 className="text-3xl font-bold text-foreground-primary">Sign in Required</h1>
            <p className="text-foreground-secondary">Log in to manage your account settings.</p>
            <button
              onClick={login}
              className="inline-flex items-center gap-2 px-8 py-3 rounded-lg font-semibold transition-smooth"
              style={{ background: '#F97316', color: '#0B0E13' }}
            >
              <LogIn size={18} />
              Sign In
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-page">
      <Header />
      <div className="flex" style={{ marginTop: '73px' }}>
        {/* Sidebar */}
        <aside className="w-64 min-h-[calc(100vh-73px)] glass-frosted border-r border-subtle p-4 hidden md:block flex-shrink-0">
          {/* User info */}
          <div className="flex items-center gap-3 px-3 py-4 mb-4 border-b border-subtle">
            {user?.avatar ? (
              <img src={user.avatar} alt="" className="w-10 h-10 rounded-full object-cover" />
            ) : (
              <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold" style={{ background: 'rgba(249,115,22,0.15)', color: '#F97316' }}>
                {(user?.name || 'U').charAt(0).toUpperCase()}
              </div>
            )}
            <div className="min-w-0">
              <p className="text-sm font-semibold text-foreground-primary truncate">{user?.name}</p>
              <p className="text-xs text-foreground-muted truncate">{user?.email}</p>
            </div>
          </div>

          {/* Nav */}
          <nav className="space-y-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm font-medium transition-smooth ${
                  activeTab === tab.id
                    ? 'text-foreground-primary bg-subtle'
                    : 'text-foreground-secondary hover:text-foreground-primary hover:bg-subtle'
                }`}
                style={activeTab === tab.id ? { borderLeft: '3px solid #F97316' } : { borderLeft: '3px solid transparent' }}
              >
                <tab.icon size={16} style={activeTab === tab.id ? { color: '#F97316' } : {}} />
                {tab.label}
              </button>
            ))}
          </nav>
        </aside>

        {/* Mobile tab bar */}
        <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 glass-frosted border-t border-subtle flex">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex flex-col items-center gap-1 py-3 text-xs font-medium transition-smooth ${
                activeTab === tab.id ? 'text-foreground-primary' : 'text-foreground-muted'
              }`}
            >
              <tab.icon size={18} style={activeTab === tab.id ? { color: '#F97316' } : {}} />
              {tab.label.split(' ')[0]}
            </button>
          ))}
        </div>

        {/* Content */}
        <main className="flex-1 p-6 md:p-8 min-h-[calc(100vh-73px)] pb-24 md:pb-8">
          <div className="max-w-3xl">
            {activeTab === 'account' && <AccountSettings />}
            {activeTab === 'billing' && <BillingSection />}
            {activeTab === 'payment' && <PaymentSettings />}
            {activeTab === 'team' && <TeamManagement />}
          </div>
        </main>
      </div>
    </div>
  );
};

export default AccountDashboard;
